﻿import React, { FC, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { BigHead } from "extended-bigheads";
import { Button, Input, Switch, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined, InfoCircleOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import { useAlert } from "../../hooks/useAlert";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissionKeys";
import {
  getLanguageName,
  isLanguageExcludedAsTargetWhenSourceIs,
} from "../../utils/languageUtils";
import { getDictDataListSimpleApi, getTargetLanguageDictListSimpleApi } from "../../service/dict";
import { triggerMembershipRefresh } from "../../service/membership";
import "./AddLanguageModal.scss";
import { addSeriesLanguagesBatchApi } from "../../service/series";
import { checkAllSpeakersGlobalApi, getSpeakersBySeriesApi } from "../../service/speakers";
import { postTaskPointPreviewApi, toTerminologyJson } from "../../service/task";
import { resolvePublicVoicePreviewFullUrl } from "../../service/voicePublic";
import { audioManager } from "../../utils/audioManager";
import { CoinsEstimateIcon } from "./CoinsEstimateIcon";
import { SearchFilterBarActionButton } from "./SearchFilterBarActionButton";
import { AutoModel } from "./AutoModel";
import { AlertMessage } from "./AlertMessage";
import { fetchMineVoiceListMulti } from "../../pages/voicePublic/mineVoiceMultiFetch";
import { fetchPublicVoiceListMulti } from "../../pages/voicePublic/publicVoiceMultiFetch";
import { speakerIdToBigHeadProps } from "../../pages/voiceLibrary/bigHeadSpeakerConfig";

type DubbingMethod = "clone" | "library" | "none";

/** 头像渐变色（与 voicePublic 一致） */
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
  "linear-gradient(135deg, #fce7f3, #fbcfe8)",
  "linear-gradient(135deg, #dbeafe, #bfdbfe)",
  "linear-gradient(135deg, #fef3c7, #fde68a)",
];

/** 基于种子字符串生成稳定的波纹高度数组（与 VoiceDesignWorkspacePage 一致） */
function waveBarsFromSeed(seed: string): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const out: number[] = [];
  for (let i = 0; i < 8; i++) {
    h = (h * 1103515245 + 12345) | 0;
    out.push(4 + (Math.abs(h) % 12));
  }
  return out;
}

function disposeVoicePreviewAudio(el: HTMLAudioElement | null) {
  if (!el) return;
  el.onended = null;
  el.onerror = null;
  try {
    el.pause();
    el.currentTime = 0;
    el.removeAttribute("src");
    el.load();
  } catch {
    // ignore
  }
}

type CharacterVoiceProfile = {
  name: string;
  description?: string;
  id?: number;
  speakCount?: number;
};

type VoiceLibraryItem = {
  id: string;
  rawId: number;
  source: "public" | "mine";
  previewUrl: string;
  name: string;
  description?: string;
  lang: string;
  age: string;
  gender: string;
  category: string;
};

type VoiceLibraryTab = "public" | "mine";
type VoiceFilterKey = "lang" | "gender" | "age" | "timbre";
type SpeakerVoiceToneBinding = {
  speakerId: number;
  voiceSourceType: "PUBLIC_VOICE_TONE" | "USER_VOICE_TONE";
  voiceToneId: number;
};

const getCharacterVoiceKey = (character: CharacterVoiceProfile, index = 0): string => {
  const id = Number(character.id);
  if (Number.isFinite(id) && id > 0) return `speaker-${id}`;
  return `name-${String(character.name || "").trim()}-${index}`;
};

function pickDefaultTermSourceLang(
  listContextLangCode: string | null | undefined,
  langs: { code: string; label: string }[]
): string {
  const ctx = listContextLangCode?.trim();
  if (!ctx) return "en";
  const hit = langs.find((l) => l.code.toLowerCase() === ctx.toLowerCase());
  return hit?.code ?? ctx;
}

/** 精品多语种「新增语种」：仅根据 code/精确中文 label 识别中英 chip（不做模糊 includes，避免误判） */
function isChineseOrEnglishLanguageOption(lang: { code: string; label: string }): boolean {
  const c = String(lang.code || "").trim().toLowerCase();
  if (c === "zh" || c.startsWith("zh-")) return true;
  if (c === "en" || c.startsWith("en-")) return true;
  const lb = String(lang.label || "").trim();
  return lb === "中文" || lb === "汉语" || lb === "英语" || lb === "英文";
}

const addLanguageModalPanelClass =
  "add-language-modal--shell [&_.auto-model__header]:!mb-3 [&_.auto-model__body]:!gap-0";

/** 解析新增语种提交成功 data（retranslateEpisode / add-languages-batch 等） */
function parseAddLanguageSubmitSuccessData(result: { data?: unknown }): {
  seriesId?: number;
  seriesName?: string;
} {
  const d = result.data;
  if (d && typeof d === "object" && !Array.isArray(d) && "seriesId" in d) {
    const seriesId = Number((d as { seriesId: unknown }).seriesId);
    const rawName = (d as { seriesName?: unknown }).seriesName;
    const seriesName = rawName != null ? String(rawName).trim() : "";
    return {
      seriesId: Number.isFinite(seriesId) && seriesId > 0 ? seriesId : undefined,
      seriesName: seriesName || undefined,
    };
  }
  if (typeof d === "number" && Number.isFinite(d) && d > 0) {
    return { seriesId: d };
  }
  if (Array.isArray(d) && d.length > 0) {
    const v = d[0];
    if (typeof v === "number" && Number.isFinite(v) && v > 0) {
      return { seriesId: v };
    }
  }
  return {};
}

/** 新增语种成功后的回调参数 */
export interface AddLanguageModalSuccessPayload {
  selectedNewLangCodes: string[];
  /** 二次译制成功后接口返回的新外层剧集 */
  newOuterSeriesId?: number;
  newOuterSeriesName?: string;
}

export interface AddLanguageModalProps {
  open: boolean;
  onCancel: () => void;
  /** 剧集 ID，必填 */
  seriesId: string | null;
  /** 获取要新增语种的任务 ID 列表（由调用方按业务传入，如已选视频 ID 或已完成任务 ID） */
  getTaskIds?: () => Promise<number[]>;
  /** 成功后回调（如刷新列表、清空选中、跳转等） */
  onSuccess?: (payload?: AddLanguageModalSuccessPayload) => void | Promise<void>;
  /** 打开弹窗时 AI 音频是否默认开启 */
  defaultAiVoice?: boolean;
  /** taskIds 为空时的提示文案（不传则使用默认「您未选中视频，请选中后操作」） */
  emptyTaskIdsMessage?: string;
  /** 需要禁用的语种编码列表（如剧集下已存在语种） */
  disabledLanguageCodes?: string[];
  /** 剧集源语言编码（如 zh）；目标语列表来自接口时仍用其筛掉与源语相同的项 */
  sourceLangCode?: string | null;
  /**
   * 多语种列表当前 tab 的语种 code（如 URL `langCode`）。有值时：提示条优先展示该语种；
   * 目标语 chip / 已选清理**仅**按本字段排除（不再叠加剧集源语，避免当前为法语时把中文一并筛掉）；
   * 术语默认行/新增行默认语种用该 code。
   */
  listContextLangCode?: string | null;
  /**
   * 目标语字典类型（GET /system/dict-data/list-simple 的 dictType）。
   * 精品译制默认 target_language；一键速译传 erp_language，与快译创建弹窗一致。
   */
  targetLanguageDictType?: "target_language" | "erp_language";
  /** 自定义提交（如一键速译可走专用接口）；返回格式需包含 code/msg */
  onSubmitAddLanguages?: (params: {
    seriesId: number;
    selectedNewLangCodes: string[];
    taskIds: number[];
    transTerms: Array<{ langCode: string; terminology: string }>;
    aiVoiceEnabled: boolean;
    needRemovalSubtitle: boolean;
    dubbingMethod?: 1 | 2 | 3;
    speakerVoiceToneBindings?: SpeakerVoiceToneBinding[];
    renderTaskId?: number | null;
    sourceLangCode?: string;
  }) => Promise<{ code: number; msg?: string; data?: unknown; newOuterSeriesId?: number }>;
  /**
   * 一键速译等：不展示「翻译术语设置」「AI配音」；提交时 transTerms 为空、不传 AI 音频。
   */
  omitTerminologyAndAiVoice?: boolean;
  /** 弹窗标题，默认「新增语种」 */
  title?: string;
  /**
   * 为 true 时提示条为「以 xxx 为源语言开始译制目标语种」；
   * 默认 false 时为「以 xxx 为源语言新增目标翻译语种」（多语种 tab 等）。
   */
  hintUseStartTranslateWording?: boolean;
  /** 成功时顶部提示优先展示接口返回的 msg（无 msg 时用默认文案） */
  preferResponseMsgOnSuccess?: boolean;
  /** 仅精品「多语种 tab → 新增语种」为 true：chip 只固定禁用中文/英语；本模式下不再用 disabledLanguageCodes 置灰（避免已存在日语等被误禁） */
  disableZhEnInMultilingualAddModal?: boolean;
  /** 仅精品多语种「二次译制 · 新增语种」：展示「字幕擦除」开关（开始译制弹窗勿传） */
  showSubtitleRemovalToggle?: boolean;
  /** 打开弹窗时「字幕擦除」初始值，通常取剧集 needRemovalSubtitle */
  defaultNeedRemovalSubtitle?: boolean;
  showDubbingMethodConfig?: boolean;
  characterVoiceProfiles?: CharacterVoiceProfile[];
  renderTaskId?: number | null;
}

export const AddLanguageModal: FC<AddLanguageModalProps> = ({
  open,
  onCancel,
  seriesId,
  getTaskIds,
  onSuccess,
  defaultAiVoice = true,
  emptyTaskIdsMessage = "您未选中视频，请选中后操作",
  disabledLanguageCodes = [],
  sourceLangCode,
  listContextLangCode,
  targetLanguageDictType = "target_language",
  onSubmitAddLanguages,
  omitTerminologyAndAiVoice = false,
  title = "新增语种",
  hintUseStartTranslateWording = false,
  preferResponseMsgOnSuccess = false,
  disableZhEnInMultilingualAddModal = false,
  showSubtitleRemovalToggle = false,
  defaultNeedRemovalSubtitle = false,
  showDubbingMethodConfig = false,
  characterVoiceProfiles = [],
  renderTaskId = null,
}) => {
  const { alertConfig, showSuccess, showError, showWarning, showInfo, hideAlert } = useAlert();
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW) === true;

  const [selectedNewLangCodes, setSelectedNewLangCodes] = useState<string[]>([]);
  const [needRemovalSubtitle, setNeedRemovalSubtitle] = useState(defaultNeedRemovalSubtitle);
  const [dubbingMethod, setDubbingMethod] = useState<DubbingMethod>("clone");
  const [canUseVoiceLibraryDubbing, setCanUseVoiceLibraryDubbing] = useState<boolean | null>(null);
  const [voiceLibraryUnavailableMessage, setVoiceLibraryUnavailableMessage] = useState("音色库配音当前不可用，请检查全局说话人配置");
  const [checkingVoiceLibraryDubbing, setCheckingVoiceLibraryDubbing] = useState(false);
  const [voiceLibraryOpen, setVoiceLibraryOpen] = useState(false);
  const [activeVoiceCharacter, setActiveVoiceCharacter] = useState<string | null>(null);
  const [activeVoiceCharacterName, setActiveVoiceCharacterName] = useState<string | null>(null);
  const [selectedCharacterVoices, setSelectedCharacterVoices] = useState<Record<string, VoiceLibraryItem>>({});
  const [voiceLibraryTab, setVoiceLibraryTab] = useState<VoiceLibraryTab>("public");
  const [voiceSearchInput, setVoiceSearchInput] = useState("");
  const [debouncedVoiceSearch, setDebouncedVoiceSearch] = useState("");
  const [voiceLangFilter, setVoiceLangFilter] = useState("");
  const [voiceGenderFilter, setVoiceGenderFilter] = useState("");
  const [voiceAgeFilter, setVoiceAgeFilter] = useState("");
  const [voiceTimbreFilter, setVoiceTimbreFilter] = useState("");
  const [voiceLibraryItems, setVoiceLibraryItems] = useState<VoiceLibraryItem[]>([]);
  const [voiceLibraryLoading, setVoiceLibraryLoading] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [openVoiceFilterKey, setOpenVoiceFilterKey] = useState<VoiceFilterKey | null>(null);
  const [voiceLangOptions, setVoiceLangOptions] = useState<Dict.DictItem[]>([]);
  // 从接口获取的角色说话人列表
  const [seriesSpeakers, setSeriesSpeakers] = useState<Array<{
    id: number;
    speakerName: string;
    isSeriesSpeaker: boolean;
    description: string | null;
    seriesId: number;
    langCode?: string;
    speakCount?: number | null;
    voiceSourceType?: string | null;
    voiceToneId?: number | null;
    voiceToneName?: string | null;
  }>>([]);
  const [voiceGenderOptions, setVoiceGenderOptions] = useState<Dict.DictItem[]>([]);
  const [voiceAgeOptions, setVoiceAgeOptions] = useState<Dict.DictItem[]>([]);
  const [voiceTimbreOptions, setVoiceTimbreOptions] = useState<Dict.DictItem[]>([]);
  const resolvedAiVoiceEnabled = showDubbingMethodConfig
    ? dubbingMethod === "clone"
    : omitTerminologyAndAiVoice
      ? false
      : defaultAiVoice;
  const [styleConfigs, setStyleConfigs] = useState<{ sourceLang: string; text: string }[]>([
    { sourceLang: "en", text: "" },
  ]);
  /** list-simple 字典；成功则用 value/name，失败或空则回退静态精品列表 */
  const [dictTargetLanguages, setDictTargetLanguages] = useState<{ code: string; label: string }[] | null>(null);
  const [previewEstimatedPoint, setPreviewEstimatedPoint] = useState<number | null>(null);
  const pointPreviewReqIdRef = useRef(0);
  const voiceLibraryCheckReqIdRef = useRef(0);
  const voiceLibraryListReqIdRef = useRef(0);
  const voiceLibraryItemsRef = useRef<VoiceLibraryItem[]>([]);
  const voiceFilterBarRef = useRef<HTMLDivElement | null>(null);
  const voicePreviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const getTaskIdsRef = useRef(getTaskIds);
  getTaskIdsRef.current = getTaskIds;
  voiceLibraryItemsRef.current = voiceLibraryItems;

  const availableLanguages = useMemo(() => {
    if (dictTargetLanguages && dictTargetLanguages.length > 0) return dictTargetLanguages;
    return [];
  }, [dictTargetLanguages]);

  const allLanguagesRecord = useMemo(() => {
    const r: Record<string, string> = {};
    availableLanguages.forEach((l) => {
      r[l.code] = l.label;
    });
    return r;
  }, [availableLanguages]);

  const isTargetExcludedBySourceFilters = useCallback(
    (code: string, label: string) => {
      // 多语种列表 tab：仅以当前 tab 语种为「源侧」排除 chip，避免再叠加剧集源语把中文等一并筛掉
      if (listContextLangCode?.trim()) {
        return isLanguageExcludedAsTargetWhenSourceIs(code, label, listContextLangCode, allLanguagesRecord);
      }
      return isLanguageExcludedAsTargetWhenSourceIs(code, label, sourceLangCode, allLanguagesRecord);
    },
    [sourceLangCode, listContextLangCode, allLanguagesRecord]
  );

  const selectableNewLanguages = useMemo(
    () => availableLanguages.filter((lang) => !isTargetExcludedBySourceFilters(lang.code, lang.label)),
    [availableLanguages, isTargetExcludedBySourceFilters]
  );
  const disabledLanguageCodeSet = new Set(disabledLanguageCodes);
  const displayedCharacterVoiceProfiles = useMemo<CharacterVoiceProfile[]>(() => {
    if (showDubbingMethodConfig) {
      return seriesSpeakers.map(s => ({
        name: s.speakerName,
        description: s.description || undefined,
        id: s.id,
        speakCount: s.speakCount ?? undefined,
      }));
    }
    return characterVoiceProfiles.filter((item) => item.name?.trim());
  }, [characterVoiceProfiles, seriesSpeakers, showDubbingMethodConfig]);
  const characterVoicePalette = ["#6366f1", "#ec4899", "#f59e0b", "#8b5cf6", "#14b8a6", "#ef4444"];

  const isChipFixedDisabledZhEn = useCallback(
    (code: string, label: string) =>
      disableZhEnInMultilingualAddModal && isChineseOrEnglishLanguageOption({ code, label }),
    [disableZhEnInMultilingualAddModal]
  );

  const hintDisplayCode = useMemo(
    () => (listContextLangCode?.trim() || sourceLangCode?.trim()) ?? "",
    [listContextLangCode, sourceLangCode]
  );
  const hintDisplayLabel = useMemo(() => {
    if (!hintDisplayCode) return "";
    return allLanguagesRecord[hintDisplayCode] ?? getLanguageName(hintDisplayCode, allLanguagesRecord) ?? hintDisplayCode;
  }, [hintDisplayCode, allLanguagesRecord]);

  const prevOpenForDefaultsRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenForDefaultsRef.current) {
      if (showSubtitleRemovalToggle) {
        setNeedRemovalSubtitle(!!defaultNeedRemovalSubtitle);
      }
    }
    prevOpenForDefaultsRef.current = open;
  }, [open, showSubtitleRemovalToggle, defaultNeedRemovalSubtitle]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res =
          targetLanguageDictType === "erp_language"
            ? await getDictDataListSimpleApi("erp_language")
            : await getTargetLanguageDictListSimpleApi();
        if (cancelled) return;
        if (res?.code === 0 && Array.isArray(res.data) && res.data.length > 0) {
          const seen = new Set<string>();
          const opts: { code: string; label: string }[] = [];
          for (const row of res.data) {
            const code = String(row.value ?? "").trim();
            if (!code || seen.has(code)) continue;
            seen.add(code);
            opts.push({ code, label: String(row.name ?? code).trim() || code });
          }
          setDictTargetLanguages(opts.length > 0 ? opts : null);
        } else {
          setDictTargetLanguages(null);
        }
      } catch (e) {
        console.error(`拉取 ${targetLanguageDictType} 字典失败:`, e);
        if (!cancelled) setDictTargetLanguages(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, targetLanguageDictType]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedVoiceSearch(voiceSearchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [voiceSearchInput]);

  useEffect(() => {
    if (!openVoiceFilterKey) return;
    const handleMouseDown = (event: MouseEvent) => {
      const root = voiceFilterBarRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setOpenVoiceFilterKey(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenVoiceFilterKey(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openVoiceFilterKey]);

  useEffect(() => {
    if (!voiceLibraryOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
          getDictDataListSimpleApi("erp_language"),
          getDictDataListSimpleApi("system_user_sex"),
          getDictDataListSimpleApi("age_category"),
          getDictDataListSimpleApi("voice_type"),
        ]);
        if (cancelled) return;
        setVoiceLangOptions(langRes.code === 0 && Array.isArray(langRes.data) ? langRes.data : []);
        setVoiceGenderOptions(genderRes.code === 0 && Array.isArray(genderRes.data) ? genderRes.data : []);
        setVoiceAgeOptions(ageRes.code === 0 && Array.isArray(ageRes.data) ? ageRes.data : []);
        setVoiceTimbreOptions(timbreRes.code === 0 && Array.isArray(timbreRes.data) ? timbreRes.data : []);
      } catch (error) {
        console.error("获取音色筛选字典失败:", error);
        if (!cancelled) {
          setVoiceLangOptions([]);
          setVoiceGenderOptions([]);
          setVoiceAgeOptions([]);
          setVoiceTimbreOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [voiceLibraryOpen]);

  const mapVoiceToneToLibraryItem = useCallback(
    (item: VoicePublic.VoiceToneItem, source: VoiceLibraryTab): VoiceLibraryItem => ({
      id: `${source}-${item.id}`,
      rawId: item.id,
      source,
      previewUrl: resolvePublicVoicePreviewFullUrl(item) || "",
      name: item.voiceName || "-",
      description: item.description || "",
      lang: item.soundTextValue || getLanguageName(item.langCode || "", allLanguagesRecord) || item.langCode || "-",
      age: item.ageTextValue || item.ageValue || "-",
      gender: item.genderTextValue || item.genderValue || "-",
      category: item.timbreTextValue || item.timbreValue || "-",
    }),
    []
  );

  useEffect(() => {
    if (!voiceLibraryOpen) {
      voiceLibraryListReqIdRef.current += 1;
      setVoiceLibraryItems([]);
      setVoiceLibraryLoading(false);
      return;
    }

    const reqId = ++voiceLibraryListReqIdRef.current;
    if (voiceLibraryItemsRef.current.length === 0) {
      setVoiceLibraryLoading(true);
    }

    (async () => {
      try {
        const langs = voiceLangFilter ? [voiceLangFilter] : [];
        const genders = voiceGenderFilter ? [voiceGenderFilter] : [];
        const ages = voiceAgeFilter ? [voiceAgeFilter] : [];
        const timbres = voiceTimbreFilter ? [voiceTimbreFilter] : [];
        const list =
          voiceLibraryTab === "public"
            ? await fetchPublicVoiceListMulti(debouncedVoiceSearch, langs, genders, ages, timbres)
            : await fetchMineVoiceListMulti(debouncedVoiceSearch, langs, genders, ages, timbres);

        if (reqId !== voiceLibraryListReqIdRef.current) return;
        setVoiceLibraryItems(list.map((item) => mapVoiceToneToLibraryItem(item, voiceLibraryTab)));
      } catch (error) {
        console.error("获取音色库列表失败:", error);
        if (reqId === voiceLibraryListReqIdRef.current) {
          setVoiceLibraryItems([]);
        }
      } finally {
        if (reqId === voiceLibraryListReqIdRef.current) {
          setVoiceLibraryLoading(false);
        }
      }
    })();
  }, [
    voiceLibraryOpen,
    voiceLibraryTab,
    debouncedVoiceSearch,
    voiceLangFilter,
    voiceGenderFilter,
    voiceAgeFilter,
    voiceTimbreFilter,
    mapVoiceToneToLibraryItem,
  ]);

  useEffect(() => {
    if (!open) return;
    setSelectedNewLangCodes((prev) => {
      const next = prev.filter((c) => {
        const label = allLanguagesRecord[c];
        if (!label) return false;
        if (isTargetExcludedBySourceFilters(c, label)) return false;
        if (isChipFixedDisabledZhEn(c, label)) return false;
        return true;
      });
      if (next.length === prev.length && next.every((code, i) => code === prev[i])) return prev;
      return next;
    });
  }, [open, sourceLangCode, listContextLangCode, allLanguagesRecord, isTargetExcludedBySourceFilters, isChipFixedDisabledZhEn]);

  const termLangInitRef = useRef(false);
  useEffect(() => {
    if (!open) {
      termLangInitRef.current = false;
      return;
    }
    if (termLangInitRef.current) return;
    termLangInitRef.current = true;
    const code = pickDefaultTermSourceLang(listContextLangCode, availableLanguages);
    setStyleConfigs([{ sourceLang: code, text: "" }]);
  }, [open, listContextLangCode, availableLanguages]);

  useEffect(() => {
    if (!open) {
      pointPreviewReqIdRef.current += 1;
      setPreviewEstimatedPoint(null);
    }
  }, [open]);

  useEffect(() => {
    if (voiceLibraryOpen) return;
    disposeVoicePreviewAudio(voicePreviewAudioRef.current);
    voicePreviewAudioRef.current = null;
    setPlayingVoiceId(null);
  }, [voiceLibraryOpen]);

  useEffect(() => {
    return () => {
      disposeVoicePreviewAudio(voicePreviewAudioRef.current);
      voicePreviewAudioRef.current = null;
      setPlayingVoiceId(null);
    };
  }, []);

  useEffect(() => {
    if (!open || !showDubbingMethodConfig) {
      voiceLibraryCheckReqIdRef.current += 1;
      setCheckingVoiceLibraryDubbing(false);
      setCanUseVoiceLibraryDubbing(null);
      setVoiceLibraryUnavailableMessage("音色库配音当前不可用，请检查全局说话人配置");
      return;
    }

    const seriesIdNum = Number(seriesId);
    if (!Number.isFinite(seriesIdNum) || seriesIdNum <= 0) {
      setCanUseVoiceLibraryDubbing(false);
      setVoiceLibraryUnavailableMessage("缺少剧集ID，无法检查全局说话人配置");
      return;
    }

    const reqId = ++voiceLibraryCheckReqIdRef.current;
    setCheckingVoiceLibraryDubbing(true);

    (async () => {
      try {
        const resolveTaskIds = getTaskIdsRef.current;
        const taskIds = resolveTaskIds ? await resolveTaskIds() : [];
        if (reqId !== voiceLibraryCheckReqIdRef.current) return;

        const validTaskIds = taskIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0);
        if (validTaskIds.length === 0) {
          setCanUseVoiceLibraryDubbing(false);
          setVoiceLibraryUnavailableMessage("缺少任务ID，无法检查全局说话人配置");
          return;
        }

        const results = await Promise.all(
          validTaskIds.map((taskId) => checkAllSpeakersGlobalApi(seriesIdNum, taskId))
        );
        if (reqId !== voiceLibraryCheckReqIdRef.current) return;

        const unavailableResult = results.find((res) => !(res?.code === 0 && res.data === true));
        setCanUseVoiceLibraryDubbing(!unavailableResult);
        setVoiceLibraryUnavailableMessage(
          unavailableResult?.msg || "音色库配音当前不可用，请检查全局说话人配置"
        );
      } catch (error) {
        console.error("检查音色库配音可用状态失败:", error);
        if (reqId === voiceLibraryCheckReqIdRef.current) {
          setCanUseVoiceLibraryDubbing(false);
          setVoiceLibraryUnavailableMessage("检查全局说话人配置失败，请重试");
        }
      } finally {
        if (reqId === voiceLibraryCheckReqIdRef.current) {
          setCheckingVoiceLibraryDubbing(false);
        }
      }
    })();
  }, [open, seriesId, showDubbingMethodConfig]);

  // 从接口获取角色说话人列表（剧集角色音色配置）
  useEffect(() => {
    if (!open || !showDubbingMethodConfig || !seriesId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getSpeakersBySeriesApi(Number(seriesId));
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          const nextSpeakers = res.data.map((speaker) => ({
            id: speaker.id,
            speakerName: speaker.speaker_name,
            isSeriesSpeaker: !!speaker.is_series_speaker,
            description: speaker.description || null,
            seriesId: speaker.series_id,
            speakCount: speaker.speak_count ?? null,
            voiceSourceType: speaker.voice_source_type ?? null,
            voiceToneId: speaker.voice_tone_id ?? null,
            voiceToneName: speaker.voice_tone_name ?? null,
          }));
          setSeriesSpeakers(nextSpeakers);
          setSelectedCharacterVoices(
            nextSpeakers.reduce<Record<string, VoiceLibraryItem>>((acc, speaker, index) => {
              if (!speaker.voiceToneId || !speaker.voiceToneName) return acc;
              const source = speaker.voiceSourceType === "USER_VOICE_TONE" ? "mine" : "public";
              acc[getCharacterVoiceKey({ name: speaker.speakerName, id: speaker.id }, index)] = {
                id: `${source}-${speaker.voiceToneId}`,
                rawId: speaker.voiceToneId,
                source,
                previewUrl: "",
                name: speaker.voiceToneName,
                lang: "",
                age: "",
                gender: "",
                category: "",
              };
              return acc;
            }, {})
          );
        } else {
          setSeriesSpeakers([]);
          setSelectedCharacterVoices({});
        }
      } catch (e) {
        console.error("获取角色说话人列表失败:", e);
        if (!cancelled) {
          setSeriesSpeakers([]);
          setSelectedCharacterVoices({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, showDubbingMethodConfig, seriesId]);

  useEffect(() => {
    if (dubbingMethod === "library" && canUseVoiceLibraryDubbing === false) {
      setDubbingMethod("clone");
    }
  }, [dubbingMethod, canUseVoiceLibraryDubbing]);

  const fetchPointPreview = useCallback(
    async (overrides?: { needRemovalSubtitle?: boolean }) => {
      if (!open || !seriesId) return;
      const seriesIdNum = parseInt(seriesId, 10);
      if (!Number.isFinite(seriesIdNum)) return;

      const removalForPreview = overrides?.needRemovalSubtitle ?? needRemovalSubtitle;

      /** 一键速译：刚打开弹窗未选语种时不请求预估，避免空 targetLangCodes 打接口 */
      if (omitTerminologyAndAiVoice && selectedNewLangCodes.length === 0) {
        pointPreviewReqIdRef.current += 1;
        setPreviewEstimatedPoint(null);
        return;
      }

      if (!omitTerminologyAndAiVoice && selectedNewLangCodes.length === 0) {
        pointPreviewReqIdRef.current += 1;
        setPreviewEstimatedPoint(null);
        return;
      }

      const reqId = ++pointPreviewReqIdRef.current;

      try {
        let res: Awaited<ReturnType<typeof postTaskPointPreviewApi>>;
        if (omitTerminologyAndAiVoice) {
          res = await postTaskPointPreviewApi({
            seriesId: seriesIdNum,
            priceType: "short_drama_tencent_mps",
            targetLangCodes: selectedNewLangCodes,
          });
        } else {
          let taskIds: number[] = [];
          const resolveTaskIds = getTaskIdsRef.current;
          if (resolveTaskIds) {
            try {
              taskIds = await resolveTaskIds();
            } catch {
              taskIds = [];
            }
          }
          if (reqId !== pointPreviewReqIdRef.current) return;
          res = await postTaskPointPreviewApi({
            seriesId: seriesIdNum,
            langCodes: selectedNewLangCodes,
            taskIds,
            aiVoiceover: resolvedAiVoiceEnabled,
            ...(showSubtitleRemovalToggle ? { needRemovalSubtitle: removalForPreview } : {}),
          });
        }

        if (reqId !== pointPreviewReqIdRef.current) return;
        if (res?.code === 0 && res.data?.estimatedPoint != null) {
          setPreviewEstimatedPoint(res.data.estimatedPoint);
        } else {
          setPreviewEstimatedPoint(null);
        }
      } catch {
        if (reqId === pointPreviewReqIdRef.current) {
          setPreviewEstimatedPoint(null);
        }
      }
    },
    [
      open,
      seriesId,
      selectedNewLangCodes,
      resolvedAiVoiceEnabled,
      needRemovalSubtitle,
      showSubtitleRemovalToggle,
      omitTerminologyAndAiVoice,
    ]
  );

  useEffect(() => {
    void fetchPointPreview();
  }, [fetchPointPreview]);

  const toggleNewLanguage = (code: string) => {
    const label = allLanguagesRecord[code];
    if (label != null && isChipFixedDisabledZhEn(code, label)) return;
    if (!disableZhEnInMultilingualAddModal && disabledLanguageCodeSet.has(code)) return;
    setSelectedNewLangCodes((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= 5) {
        showWarning("最多只能选择5个翻译语种");
        return prev;
      }
      return [...prev, code];
    });
  };

  const handleAddStyleConfigRow = () => {
    setStyleConfigs((prev) => [
      ...prev,
      { sourceLang: pickDefaultTermSourceLang(listContextLangCode, availableLanguages), text: "" },
    ]);
  };

  const handleStyleConfigChange = (index: number, field: "sourceLang" | "text", value: string) => {
    setStyleConfigs((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const resetForm = () => {
    setSelectedNewLangCodes([]);
    setStyleConfigs([{ sourceLang: pickDefaultTermSourceLang(listContextLangCode, availableLanguages), text: "" }]);
    setNeedRemovalSubtitle(!!defaultNeedRemovalSubtitle);
    setDubbingMethod("clone");
    setVoiceLibraryOpen(false);
    setActiveVoiceCharacter(null);
    setActiveVoiceCharacterName(null);
    setSelectedCharacterVoices({});
    setVoiceLibraryTab("public");
    setVoiceSearchInput("");
    setDebouncedVoiceSearch("");
    setVoiceLangFilter("");
    setVoiceGenderFilter("");
    setVoiceAgeFilter("");
    setVoiceTimbreFilter("");
    setVoiceLibraryItems([]);
    setPreviewEstimatedPoint(null);
  };

  const handleConfirm = async () => {
    if (!seriesId) {
      showError("缺少剧集ID，无法新增语种");
      return;
    }
    if (!selectedNewLangCodes.length) {
      showWarning("请先选择要新增的语种");
      return;
    }

    try {
      let taskIds: number[] = [];
      if (onSubmitAddLanguages) {
        taskIds = getTaskIds ? await getTaskIds() : [];
        if (!omitTerminologyAndAiVoice && (!taskIds || taskIds.length === 0)) {
          showWarning(emptyTaskIdsMessage);
          return;
        }
      } else {
        taskIds = getTaskIds ? await getTaskIds() : [];
        if (!taskIds || taskIds.length === 0) {
          showWarning(emptyTaskIdsMessage);
          return;
        }
      }

      const transTerms: Array<{ langCode: string; terminology: string }> = [];
      if (!omitTerminologyAndAiVoice && styleConfigs?.length) {
        styleConfigs.forEach((config) => {
          if (config.text?.trim()) {
            transTerms.push({
              langCode: config.sourceLang,
              terminology: toTerminologyJson(config.text),
            });
          }
        });
      }

      const aiVoiceForSubmit = resolvedAiVoiceEnabled;
      const seriesIdNum = parseInt(seriesId, 10);
      const submitSourceLangCode = (listContextLangCode?.trim() || sourceLangCode?.trim() || undefined);
      const dubbingMethodForSubmit: 1 | 2 | 3 =
        dubbingMethod === "clone" ? 1 : dubbingMethod === "library" ? 2 : 3;
      const speakerVoiceToneBindings: SpeakerVoiceToneBinding[] =
        dubbingMethod === "library"
          ? displayedCharacterVoiceProfiles.reduce<SpeakerVoiceToneBinding[]>((acc, character, index) => {
              const speakerId = character.id;
              const characterName = String(character.name || "").trim();
              const selectedVoice = characterName ? selectedCharacterVoices[getCharacterVoiceKey(character, index)] : undefined;
              if (!speakerId || !selectedVoice) return acc;
              acc.push({
                speakerId,
                voiceSourceType: selectedVoice.source === "public" ? "PUBLIC_VOICE_TONE" : "USER_VOICE_TONE",
                voiceToneId: selectedVoice.rawId,
              });
              return acc;
            }, [])
          : [];
      if (dubbingMethod === "library") {
        const requiredCharacters = displayedCharacterVoiceProfiles.filter((character) => {
          const speakerId = character.id;
          const characterName = String(character.name || "").trim();
          return Boolean(speakerId && characterName);
        });
        if (requiredCharacters.length === 0) {
          showWarning("暂无可配置音色的全局说话人");
          return;
        }
        const missingNames = requiredCharacters
          .filter((character, index) => {
            const characterName = String(character.name || "").trim();
            return !selectedCharacterVoices[getCharacterVoiceKey(character, index)];
          })
          .map((character) => String(character.name || "").trim())
          .filter(Boolean);
        if (missingNames.length > 0) {
          showWarning(`请选择全局说话人音色：${missingNames.join("、")}`);
          return;
        }
      }

      showInfo("正在添加语种，请稍候...");

      const result = onSubmitAddLanguages
        ? await onSubmitAddLanguages({
            seriesId: seriesIdNum,
            selectedNewLangCodes,
            taskIds,
            transTerms,
            aiVoiceEnabled: aiVoiceForSubmit,
            needRemovalSubtitle: showSubtitleRemovalToggle ? needRemovalSubtitle : false,
            dubbingMethod: dubbingMethodForSubmit,
            ...(speakerVoiceToneBindings.length > 0 ? { speakerVoiceToneBindings } : {}),
            ...(renderTaskId != null ? { renderTaskId } : {}),
            ...(submitSourceLangCode ? { sourceLangCode: submitSourceLangCode } : {}),
          })
        : await addSeriesLanguagesBatchApi({
            seriesId: seriesIdNum,
            langCodes: selectedNewLangCodes,
            taskIds,
            ...(transTerms.length > 0 ? { transTerms } : {}),
            aiVoiceover: aiVoiceForSubmit,
            dubbingMethod: dubbingMethodForSubmit,
            ...(speakerVoiceToneBindings.length > 0 ? { speakerVoiceToneBindings } : {}),
            ...(renderTaskId != null ? { renderTaskId } : {}),
            ...(submitSourceLangCode ? { sourceLangCode: submitSourceLangCode } : {}),
          });

      if (result?.code === 0) {
        const successText = preferResponseMsgOnSuccess
          ? (result?.msg?.trim() || "操作成功")
          : "新增语种成功";
        const addedLangCodes = [...selectedNewLangCodes];
        const parsedSuccess = parseAddLanguageSubmitSuccessData(result);
        showSuccess(successText);
        onCancel();
        resetForm();
        window.dispatchEvent(new Event("points:refresh"));
        triggerMembershipRefresh();
        await onSuccess?.({
          selectedNewLangCodes: addedLangCodes,
          ...(parsedSuccess.seriesId != null && !omitTerminologyAndAiVoice
            ? {
                newOuterSeriesId: parsedSuccess.seriesId,
                ...(parsedSuccess.seriesName ? { newOuterSeriesName: parsedSuccess.seriesName } : {}),
              }
            : {}),
        });
      } else {
        showError(result?.msg || "新增语种失败，请稍后重试");
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { msg?: string } }; message?: string };
      console.error("新增语种失败:", error);
      const messageText = err?.response?.data?.msg || err?.message || "新增语种失败，请稍后重试";
      showError(messageText);
    }
  };

  const handleCancel = () => {
    onCancel();
    resetForm();
  };

  const handleOpenVoiceLibrary = (characterKey: string, characterName: string) => {
    setActiveVoiceCharacter(characterKey);
    setActiveVoiceCharacterName(characterName);
    setVoiceLibraryTab("public");
    setVoiceSearchInput("");
    setDebouncedVoiceSearch("");
    setVoiceLangFilter("");
    setVoiceGenderFilter("");
    setVoiceAgeFilter("");
    setVoiceTimbreFilter("");
    setVoiceLibraryOpen(true);
  };

  const handleSelectVoice = (voice: VoiceLibraryItem) => {
    if (activeVoiceCharacter) {
      setSelectedCharacterVoices((prev) => ({ ...prev, [activeVoiceCharacter]: voice }));
    }
    setActiveVoiceCharacterName(null);
    setVoiceLibraryOpen(false);
  };

  const handlePlayVoicePreview = (voice: VoiceLibraryItem) => {
    if (!voice.previewUrl) {
      showWarning("暂无可试听音频");
      return;
    }

    if (playingVoiceId === voice.id) {
      disposeVoicePreviewAudio(voicePreviewAudioRef.current);
      voicePreviewAudioRef.current = null;
      setPlayingVoiceId(null);
      return;
    }

    disposeVoicePreviewAudio(voicePreviewAudioRef.current);
    voicePreviewAudioRef.current = null;
    audioManager.stopAll();

    try {
      const audio = new Audio(voice.previewUrl);
      voicePreviewAudioRef.current = audio;
      audio.onended = () => {
        if (voicePreviewAudioRef.current !== audio) return;
        voicePreviewAudioRef.current = null;
        setPlayingVoiceId(null);
      };
      audio.onerror = () => {
        if (voicePreviewAudioRef.current !== audio) return;
        console.error("音色试听播放失败:", voice.previewUrl);
        voicePreviewAudioRef.current = null;
        setPlayingVoiceId(null);
        showError("音色试听播放失败");
      };
      void audio.play().catch((error) => {
        if (voicePreviewAudioRef.current !== audio) return;
        console.error("音色试听播放失败:", error);
        voicePreviewAudioRef.current = null;
        setPlayingVoiceId(null);
        showError("音色试听播放失败");
      });
      setPlayingVoiceId(voice.id);
    } catch (error) {
      console.error("音色试听播放失败:", error);
      setPlayingVoiceId(null);
      showError("音色试听播放失败");
    }
  };

  const renderDubbingMethodIcon = (method: DubbingMethod) => {
    const common = {
      className: "add-language-modal__dub-icon",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.5,
      strokeLinecap: "round" as const,
      strokeLinejoin: "round" as const,
      "aria-hidden": true,
    };
    if (method === "clone") {
      return (
        <svg {...common}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    }
    if (method === "library") {
      return (
        <svg {...common}>
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      );
    }
    return (
      <svg {...common}>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
      </svg>
    );
  };

  return (
    <>
    <AlertMessage config={alertConfig} onClose={hideAlert} />
    <AutoModel
      open={open}
      onClose={handleCancel}
      title={title}
      size="md"
      contentClassName={addLanguageModalPanelClass}
      footerLeft={
        hasPoints && previewEstimatedPoint != null ? (
          <div className="add-language-modal__point-estimate" title="以实际扣减为准">
            <CoinsEstimateIcon />
            <span className="add-language-modal__point-estimate-label">
              预计消耗 {previewEstimatedPoint ?? 0} 积分
            </span>
          </div>
        ) : null
      }
      footerRight={
        <>
          <Button key="cancel" onClick={handleCancel}>
            取消
          </Button>
          <Button key="ok" type="primary" onClick={handleConfirm}>
            确定
          </Button>
        </>
      }
    >
      <div className="add-language-modal">
        {hintDisplayCode ? (
          <div className="add-language-modal__hint">
            <InfoCircleOutlined className="add-language-modal__hint-icon" aria-hidden />
            <span>
              以 <span className="add-language-modal__hint-em">{hintDisplayLabel}</span> 为源语言
              {hintUseStartTranslateWording ? "开始译制目标语种" : "新增目标翻译语种"}
            </span>
          </div>
        ) : null}

        <div className="add-language-modal__section">
          <div className="add-language-modal__section-label">
            选择新增翻译语种<span className="add-language-modal__required">*</span>
          </div>
          <div className="add-language-modal__lang-chips">
            {selectableNewLanguages.map((lang) => {
              const active = selectedNewLangCodes.includes(lang.code);
              const chipDisabledFromParent =
                !disableZhEnInMultilingualAddModal && disabledLanguageCodeSet.has(lang.code);
              const disabled = chipDisabledFromParent || isChipFixedDisabledZhEn(lang.code, lang.label);
              return (
                <Button
                  key={lang.code}
                  type="default"
                  size="small"
                  disabled={disabled}
                  className={`add-language-modal__lang-chip${active ? " add-language-modal__lang-chip--active" : ""}`}
                  title={lang.label}
                  onClick={() => toggleNewLanguage(lang.code)}
                >
                  {lang.label}
                </Button>
              );
            })}
          </div>
        </div>

        {!omitTerminologyAndAiVoice ? (
          <>
            <div className="add-language-modal__section">
              <div className="add-language-modal__term-header">
                <span className="add-language-modal__term-title">翻译术语设置</span>
                <Button
                  type="text"
                  className="add-language-modal__term-add-btn"
                  icon={<PlusOutlined />}
                  aria-label="新增术语行"
                  onClick={handleAddStyleConfigRow}
                />
              </div>
              {styleConfigs.map((config, index) => (
                <div key={index} className="add-language-modal__term-row">
                  <Dropdown
                    trigger={["click"]}
                    placement="bottomLeft"
                    overlayClassName="add-language-modal__term-lang-dropdown"
                    getPopupContainer={(trigger) => trigger.parentElement || trigger}
                    menu={{
                      items: availableLanguages.map((lang) => ({ key: lang.code, label: lang.label })) as MenuProps["items"],
                      selectable: true,
                      selectedKeys: [config.sourceLang],
                      onClick: ({ key }) => handleStyleConfigChange(index, "sourceLang", key as string),
                    }}
                  >
                    <div className="add-language-modal__term-lang-trigger" title={allLanguagesRecord[config.sourceLang] || config.sourceLang}>
                      <span className="add-language-modal__term-lang-trigger-label">
                        {allLanguagesRecord[config.sourceLang] || config.sourceLang}
                      </span>
                      <DownOutlined className="add-language-modal__term-lang-trigger-caret" />
                    </div>
                  </Dropdown>
                  <Input.TextArea
                    value={config.text}
                    onChange={(e) => handleStyleConfigChange(index, "text", e.target.value)}
                    placeholder={'请输入翻译术语映射，如：\n"香港"->"Hong Kong";\n"小子"->"Kid";'}
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    className="add-language-modal__term-textarea"
                  />
                </div>
              ))}
            </div>
            {showSubtitleRemovalToggle ? (
              <div className="add-language-modal__ai-row">
                <span className="add-language-modal__ai-label">字幕擦除</span>
                <Switch
                  className="add-language-modal__ai-switch"
                  checked={needRemovalSubtitle}
                  onChange={(checked) => {
                    setNeedRemovalSubtitle(checked);
                    void fetchPointPreview({ needRemovalSubtitle: checked });
                  }}
                />
              </div>
            ) : null}
            {showDubbingMethodConfig ? (
              <>
                <div className="add-language-modal__section">
                  <div className="add-language-modal__section-label">配音方式</div>
                  <div className="add-language-modal__dub-method-group">
                    {[
                      { key: "clone" as const, label: "精品克隆配音", desc: "一比一还原音色" },
                      { key: "library" as const, label: "音色库配音", desc: "使用音色库预置音色" },
                      { key: "none" as const, label: "无配音", desc: "仅生成字幕，不进行配音" },
                    ].map((item) => {
                      const libraryDisabled =
                        item.key === "library" &&
                        canUseVoiceLibraryDubbing !== true;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          aria-disabled={libraryDisabled}
                          className={`add-language-modal__dub-method-btn${
                            dubbingMethod === item.key ? " add-language-modal__dub-method-btn--active" : ""
                          }${libraryDisabled ? " add-language-modal__dub-method-btn--disabled" : ""}`}
                          onClick={() => {
                            if (libraryDisabled) {
                              showWarning(
                                checkingVoiceLibraryDubbing
                                  ? "正在检查说话人校准状态，请稍后"
                                  : voiceLibraryUnavailableMessage
                              );
                              return;
                            }
                            setDubbingMethod(item.key);
                          }}
                        >
                          {renderDubbingMethodIcon(item.key)}
                          <span className="add-language-modal__dub-label">{item.label}</span>
                          <span className="add-language-modal__dub-desc">
                            {item.key === "library" && checkingVoiceLibraryDubbing ? "正在检查说话人校准状态" : item.desc}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {dubbingMethod === "library" ? (
                  <div className="add-language-modal__char-voice-section">
                    <div className="add-language-modal__char-voice-label">剧集角色音色配置</div>
                    <div className="add-language-modal__char-voice-table">
                      {displayedCharacterVoiceProfiles.length === 0 ? (
                        <div className="add-language-modal__char-voice-empty">暂无角色</div>
                      ) : displayedCharacterVoiceProfiles.map((character, index) => {
                        const name = String(character.name || "").trim();
                        if (!name) return null;
                        const characterVoiceKey = getCharacterVoiceKey(character, index);
                        const selectedVoice = selectedCharacterVoices[characterVoiceKey];
                        const speakerAvatarSeed = String(character.id ?? name);
                        return (
                          <div key={`${name}-${index}`} className="add-language-modal__char-voice-row">
                            <span
                              className="add-language-modal__char-voice-bighead"
                              aria-hidden
                            >
                              <BigHead {...speakerIdToBigHeadProps(speakerAvatarSeed)} />
                            </span>
                            <div className="add-language-modal__char-voice-info">
                              <div className="add-language-modal__char-voice-name">{name}</div>
                            </div>
                            <button
                              type="button"
                              className={`add-language-modal__char-voice-trigger${
                                selectedVoice ? " add-language-modal__char-voice-trigger--selected" : ""
                              }`}
                              onClick={() => handleOpenVoiceLibrary(characterVoiceKey, name)}
                            >
                              <span>{selectedVoice?.name || "请选择音色"}</span>
                              <span className="add-language-modal__char-voice-chevron">▼</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </AutoModel>
    <AutoModel
      open={voiceLibraryOpen}
      onClose={() => setVoiceLibraryOpen(false)}
      title={activeVoiceCharacterName ? `选择音色 - ${activeVoiceCharacterName}` : "选择音色"}
      size="md"
      contentMaxWidth={620}
      contentClassName="add-language-modal__voice-library-modal"
      bodyStyle={{ maxHeight: 520, overflowY: "hidden" }}
    >
      <div className="add-language-modal__voice-library">
        <div className="add-language-modal__voice-library-tabs">
          {[
            { key: "public" as const, label: "公共音色" },
            { key: "mine" as const, label: "我的音色" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`add-language-modal__voice-library-tab${
                voiceLibraryTab === tab.key ? " add-language-modal__voice-library-tab--active" : ""
              }`}
              onClick={() => setVoiceLibraryTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="add-language-modal__voice-filter-bar" ref={voiceFilterBarRef}>
          <input
            type="search"
            className="add-language-modal__voice-filter add-language-modal__voice-filter--search"
            value={voiceSearchInput}
            onChange={(e) => setVoiceSearchInput(e.target.value)}
            placeholder="搜索音色"
          />
          {[
            {
              key: "lang" as const,
              placeholder: "全部语种",
              value: voiceLangFilter,
              onChange: setVoiceLangFilter,
              options: voiceLangOptions,
            },
            {
              key: "gender" as const,
              placeholder: "全部性别",
              value: voiceGenderFilter,
              onChange: setVoiceGenderFilter,
              options: voiceGenderOptions,
            },
            {
              key: "age" as const,
              placeholder: "全部年龄",
              value: voiceAgeFilter,
              onChange: setVoiceAgeFilter,
              options: voiceAgeOptions,
            },
            {
              key: "timbre" as const,
              placeholder: "全部类别",
              value: voiceTimbreFilter,
              onChange: setVoiceTimbreFilter,
              options: voiceTimbreOptions,
            },
          ].map((filter) => {
            const activeOption = filter.options.find((option) => option.value === filter.value);
            const displayText = activeOption?.name || filter.placeholder;
            const isOpen = openVoiceFilterKey === filter.key;
            return (
              <div key={filter.key} className="add-language-modal__voice-filter-select">
                <button
                  type="button"
                  className={`add-language-modal__voice-filter add-language-modal__voice-filter-trigger${
                    isOpen ? " add-language-modal__voice-filter-trigger--open" : ""
                  }`}
                  onClick={() => setOpenVoiceFilterKey((prev) => (prev === filter.key ? null : filter.key))}
                  title={displayText}
                >
                  <span>{displayText}</span>
                  <DownOutlined />
                </button>
                {isOpen ? (
                  <div className="add-language-modal__voice-filter-menu">
                    <button
                      type="button"
                      className={`add-language-modal__voice-filter-option${
                        !filter.value ? " add-language-modal__voice-filter-option--active" : ""
                      }`}
                      onClick={() => {
                        filter.onChange("");
                        setOpenVoiceFilterKey(null);
                      }}
                    >
                      {filter.placeholder}
                    </button>
                    {filter.options.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`add-language-modal__voice-filter-option${
                          filter.value === option.value ? " add-language-modal__voice-filter-option--active" : ""
                        }`}
                        onClick={() => {
                          filter.onChange(option.value);
                          setOpenVoiceFilterKey(null);
                        }}
                        title={option.name}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="add-language-modal__voice-library-list">
          {voiceLibraryLoading && voiceLibraryItems.length === 0 ? (
            <div className="add-language-modal__voice-lib-empty">正在加载音色...</div>
          ) : voiceLibraryItems.length === 0 ? (
            <div className="add-language-modal__voice-lib-empty">
              {voiceLibraryTab === "public" ? "暂无公共音色" : "暂无我的音色"}
            </div>
          ) : voiceLibraryItems.map((voice, index) => {
            const selected = activeVoiceCharacter
              ? selectedCharacterVoices[activeVoiceCharacter]?.id === voice.id
              : false;
            const playing = playingVoiceId === voice.id;
            return (
              <div
                key={voice.id}
                className={`add-language-modal__voice-lib-item${
                  selected ? " add-language-modal__voice-lib-item--selected" : ""
                }`}
                onClick={() => handleSelectVoice(voice)}
              >
                <button
                  type="button"
                  className={`add-language-modal__voice-lib-play${
                    playing ? " add-language-modal__voice-lib-play--playing" : ""
                  }`}
                  title={playing ? "停止试听" : "试听"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayVoicePreview(voice);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </button>
                <span
                  className="add-language-modal__voice-lib-avatar"
                  style={{ background: AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length] }}
                >
                  <UserOutlined />
                </span>
                <span className="add-language-modal__voice-lib-info">
                  <span className="add-language-modal__voice-lib-name">{voice.name}</span>
                  {voice.description?.trim() ? (
                    <span className="add-language-modal__voice-lib-desc" title={voice.description}>
                      {voice.description}
                    </span>
                  ) : null}
                  {voice.source === "public" ? (
                    <span className="add-language-modal__voice-lib-wave" aria-hidden>
                      {waveBarsFromSeed(voice.name).map((height, index) => (
                        <span key={index} style={{ height }} />
                      ))}
                    </span>
                  ) : null}
                  <span className="add-language-modal__voice-lib-tags">
                    <span className="add-language-modal__voice-lib-tag-lang">{voice.lang}</span>
                    <span className="add-language-modal__voice-lib-tag-age">{voice.age}</span>
                    <span className="add-language-modal__voice-lib-tag-gender">{voice.gender}</span>
                    <span className="add-language-modal__voice-lib-tag-cat">{voice.category}</span>
                  </span>
                </span>
                <span className="add-language-modal__voice-lib-select">
                  {selected ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : "选择"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </AutoModel>
    </>
  );
};

/** 与 VideoList 批量操作区「新增语种」一致：使用 SearchFilterBarActionButton 文本型，仅浮起不改色 */
export const AddLanguageTriggerButton: FC<{ onClick: () => void; children?: React.ReactNode }> = ({
  onClick,
  children = "新增语种",
}) => (
  <SearchFilterBarActionButton variant="text" onClick={onClick}>
    {children}
  </SearchFilterBarActionButton>
);
